import type { HTMLAttributes, ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { Heading } from './Heading'
import styles from './Layout.module.css'

export type Gap = 0 | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline'
type Justify = 'start' | 'center' | 'end' | 'between' | 'around'

const gapClass: Record<string, string> = { 0: styles.gap0, xs: styles.gapXs, sm: styles.gapSm, md: styles.gapMd, lg: styles.gapLg, xl: styles.gapXl }
const alignClass: Record<Align, string> = { start: styles.alignStart, center: styles.alignCenter, end: styles.alignEnd, stretch: styles.alignStretch, baseline: styles.alignBaseline }
const justifyClass: Record<Justify, string> = { start: styles.justifyStart, center: styles.justifyCenter, end: styles.justifyEnd, between: styles.justifyBetween, around: styles.justifyAround }

export interface FlexProps extends HTMLAttributes<HTMLElement> {
  gap?: Gap
  align?: Align
  justify?: Justify
  wrap?: boolean
  /* flex: 1 — fills the remaining space of its parent. */
  grow?: boolean
  as?: 'div' | 'section' | 'aside' | 'nav' | 'header'
  children?: ReactNode
}

function flex(base: string, { gap = 'md', align, justify, wrap, grow, className }: FlexProps) {
  return [
    base,
    gapClass[String(gap)],
    align && alignClass[align],
    justify && justifyClass[justify],
    wrap && styles.wrap,
    grow && styles.grow,
    className,
  ].filter(Boolean).join(' ')
}

/* Vertical flex column. */
export function Stack({ gap, align, justify, wrap, grow, as: Tag = 'div', className, children, ...rest }: FlexProps) {
  return <Tag className={flex(styles.stack, { gap, align, justify, wrap, grow, className })} {...rest}>{children}</Tag>
}

/* Horizontal flex row, vertically centered by default. */
export function Row({ gap, align, justify, wrap, grow, as: Tag = 'div', className, children, ...rest }: FlexProps) {
  return <Tag className={flex(styles.row, { gap, align, justify, wrap, grow, className })} {...rest}>{children}</Tag>
}

export interface PageProps {
  /* Column width: sm forms, md content, lg tables/games, full for game tables. */
  width?: 'sm' | 'md' | 'lg' | 'full'
  children: ReactNode
}

const pageClass = { sm: styles.pageSm, md: styles.pageMd, lg: styles.pageLg, full: styles.pageFull }

/* Centered page column with the standard top offset; every page starts here. */
export function Page({ width = 'md', children }: PageProps) {
  return <div className={`${styles.page} ${pageClass[width]} deco-stagger`}>{children}</div>
}

export interface PageHeaderProps {
  title: string
  back?: { href: string; label: string }
  actions?: ReactNode
}

export function PageHeader({ title, back, actions }: PageHeaderProps) {
  return (
    <header className={styles.header}>
      <div>
        {back && <Link href={back.href} className={styles.back}>← {back.label}</Link>}
        <Heading level={1}>{title}</Heading>
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </header>
  )
}
